import React, { useEffect, useRef, useState, useCallback } from "react";
import Plotly from "plotly.js-dist-min";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMqtt } from "context/MQTTContext";

export interface RealTimeSoundLevelsProps {
    chartSettings?: any; // Replace 'any' with a specific type if available.
    recording?: boolean;
    resetMaxSignal?: number; // New prop to trigger max level reset
    referenceCurve?: number[] | null; // New prop for the reference curve
}

const RealTimeSoundLevels: React.FC<RealTimeSoundLevelsProps> = ({ chartSettings, recording, resetMaxSignal, referenceCurve, }) => {
    const { thirdOctaveData } = useMqtt();
    const [maxThirdOctaveData, setMaxThirdOctaveData] = useState<number[]>([]);
    const [fullOctaveData, setFullOctaveData] = useState<number[]>([]);
    const [maxFullOctaveData, setMaxFullOctaveData] = useState<number[]>([]);
    const thirdOctaveChartRef = useRef<HTMLDivElement>(null);
    const fullOctaveChartRef = useRef<HTMLDivElement>(null);

    const computeFullOctaveBands = useCallback((thirdData: number[]): number[] => {
        if (!thirdData || thirdData.length < 27) return [];
        const groups = [
            [0, 1, 2],
            [3, 4, 5],
            [6, 7, 8],
            [9, 10, 11],
            [12, 13, 14],
            [15, 16, 17],
            [18, 19, 20],
            [21, 22, 23],
            [24, 25, 26],
        ];
        return groups.map(group => {
            const energySum = group.reduce((acc, idx) => acc + Math.pow(10, thirdData[idx] / 10), 0);
            return 10 * Math.log10(energySum);
        });
    }, []);

    // Reset max arrays when resetMaxSignal changes.
    useEffect(() => {
        setMaxThirdOctaveData([]);
        setMaxFullOctaveData([]);
    }, [resetMaxSignal]);

    const updateThirdOctaveChart = useCallback(() => {
        if (!thirdOctaveChartRef.current) return;
        const xLabels = [
            '20', '25', '31.5', '40', '50', '63', '80', '100', '125', '160',
            '200', '250', '315', '400', '500', '630', '800', '1000', '1250',
            '1600', '2000', '2500', '3150', '4000', '5000', '6300', '8000'
        ];
        const indices = xLabels.map((_, i) => i);
        const currentTrace = {
            x: indices,
            y: thirdOctaveData,
            type: 'bar' as const,
            name: 'Third Octave Current Levels',
            width: 0.8,
            marker: { color: '#6594B3' }
        };
        const maxX: number[] = [];
        const maxY: number[] = [];
        for (let i = 0; i < maxThirdOctaveData.length; i++) {
            maxX.push(i - 0.4, i + 0.4, NaN);
            maxY.push(maxThirdOctaveData[i], maxThirdOctaveData[i], NaN);
        }
        const maxTrace = {
            x: maxX,
            y: maxY,
            type: 'scatter' as const,
            mode: 'lines' as const,
            line: { dash: 'solid' as const, color: '#156082' },
            name: 'Third Octave Max Levels'
        };
        const traces: any[] = [currentTrace, maxTrace];
        if (referenceCurve && referenceCurve.length === xLabels.length) {
            const refTrace = {
                x: indices,
                y: referenceCurve,
                type: 'scatter' as const,
                mode: 'lines' as const,
                line: { dash: 'dash', color: '#FF0000' },
                name: 'Reference Curve'
            };
            traces.push(refTrace);
        }
        const layout: Partial<Plotly.Layout> = {
            title: 'Third Octave Band Levels',
            xaxis: {
                title: 'Frequency Bands',
                tickmode: 'array',
                tickvals: indices,
                ticktext: xLabels,
            },
            yaxis: { title: 'Level (dB)' }
        };
        Plotly.newPlot(thirdOctaveChartRef.current, traces, layout, { responsive: true });
    }, [thirdOctaveData, maxThirdOctaveData, referenceCurve]);

    const updateFullOctaveChart = useCallback(() => {
        if (!fullOctaveChartRef.current) return;
        const xLabels = ['31.5', '63', '125', '250', '500', '1000', '2000', '4000', '8000'];
        const indices = xLabels.map((_, i) => i);
        const currentTrace = {
            x: indices,
            y: fullOctaveData,
            type: 'bar' as const,
            name: 'Full Octave Current Levels',
            width: 0.8,
            marker: { color: '#6594B3' }
        };
        const maxX: number[] = [];
        const maxY: number[] = [];
        for (let i = 0; i < maxFullOctaveData.length; i++) {
            maxX.push(i - 0.4, i + 0.4, NaN);
            maxY.push(maxFullOctaveData[i], maxFullOctaveData[i], NaN);
        }
        const maxTrace = {
            x: maxX,
            y: maxY,
            type: 'scatter' as const,
            mode: 'lines' as const,
            line: { dash: 'solid' as const, color: '#156082' },
            name: 'Full Octave Max Levels'
        };
        const layout: Partial<Plotly.Layout> = {
            title: 'Full Octave Band Levels',
            xaxis: {
                title: 'Frequency Bands',
                tickmode: 'array',
                tickvals: indices,
                ticktext: xLabels,
            },
            yaxis: { title: 'Level (dB)' }
        };
        Plotly.newPlot(fullOctaveChartRef.current, [currentTrace, maxTrace], layout, { responsive: true });
    }, [fullOctaveData, maxFullOctaveData]);

    // Update computed values and max tracking whenever thirdOctaveData changes.
    useEffect(() => {
        console.log("thirdOctaveData:", thirdOctaveData);
        if (thirdOctaveData.length > 0) {
            setMaxThirdOctaveData(prevMax => {
                if (!prevMax || prevMax.length === 0) return thirdOctaveData;
                return thirdOctaveData.map((val, idx) => Math.max(val, prevMax[idx] ?? 0));
            });
            const computedFull = computeFullOctaveBands(thirdOctaveData);
            setFullOctaveData(computedFull);
            setMaxFullOctaveData(prevMax => {
                if (!prevMax || prevMax.length === 0) return computedFull;
                return computedFull.map((val, idx) => Math.max(val, prevMax[idx] ?? 0));
            });
        }
    }, [thirdOctaveData, computeFullOctaveBands]);

    useEffect(() => {
        if (thirdOctaveData.length > 0 && maxThirdOctaveData.length > 0) {
            updateThirdOctaveChart();
        }
    }, [thirdOctaveData, maxThirdOctaveData, updateThirdOctaveChart]);

    useEffect(() => {
        if (fullOctaveData.length > 0 && maxFullOctaveData.length > 0) {
            updateFullOctaveChart();
        }
    }, [fullOctaveData, maxFullOctaveData, updateFullOctaveChart]);

    return (
        <MDBox sx={{ padding: '16px', boxSizing: 'border-box' }}>
            <MDTypography variant="h6">Sound Levels</MDTypography>
            {/* Third Octave Band Chart */}
            <MDBox sx={{ marginTop: '24px' }}>
                <div ref={thirdOctaveChartRef} style={{ width: "100%", height: "600px" }} />
            </MDBox>
            {/* Full Octave Band Chart */}
            <MDBox sx={{ marginTop: '24px' }}>
                <div ref={fullOctaveChartRef} style={{ width: "100%", height: "400px" }} />
            </MDBox>
        </MDBox>
    );
};

export default RealTimeSoundLevels;
